import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getVariant, updateVariant, createHomework, reorderHomeworks } from '../../services/adminService';
import HomeworkNumberEditor from './HomeworkNumberEditor';
import Modal from '../Common/Modal';
import toast from 'react-hot-toast';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Баллы по умолчанию: ЕГЭ №1–25 = 1, №26–27 = 2. ОГЭ — 1 (вручную).
const defaultMax = (examType, idx0) => (examType === 'ЕГЭ' && idx0 >= 25 ? 2 : 1);
const plural = (n, forms) => {
  const a = Math.abs(n) % 100, b = a % 10;
  if (a > 10 && a < 20) return forms[2];
  if (b > 1 && b < 5) return forms[1];
  if (b === 1) return forms[0];
  return forms[2];
};

// Карточка номера с перетаскиванием (тянуть за строку-шапку).
const SortableNumber = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: 'hw-' + props.homework.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0 : 1 };
  return <HomeworkNumberEditor {...props} dragRef={setNodeRef} dragStyle={style} rowProps={{ ...attributes, ...listeners }} />;
};

// Открытый вариант: без модулей — номера сразу идут в строку и раскрываются.
// Редактор номера — тот же, что и в ДЗ (+ поле «Баллы»). Автосохранение, дублирование, перетаскивание.
const VariantNumbersPane = ({ variantId, onBack, onMetaChanged, icon, webinars = [] }) => {
  const [variant, setVariant] = useState(null);
  const [nomera, setNomera] = useState([]);
  const [title, setTitle] = useState('');
  const [openIds, setOpenIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [linkOpen, setLinkOpen] = useState(false);
  const [published, setPublished] = useState(false);
  const pubRef = useRef(false);
  const suppressClick = useRef(false); // гасим клик-тоггл сразу после перетаскивания

  const load = useCallback(async () => {
    const res = await getVariant(variantId);
    const v = res.data;
    setVariant(v);
    setTitle(v.title || '');
    setNomera(v.Homeworks || v.Homework || []);
    setPublished(!!v.isPublished); pubRef.current = !!v.isPublished;
    return v;
  }, [variantId]);

  useEffect(() => { setLoading(true); setOpenIds(new Set()); load().catch(() => {}).finally(() => setLoading(false)); }, [load]);

  // Любое изменение опубликованного варианта снимает с публикации (появляется облако).
  const maybeUnpublish = useCallback(async () => {
    if (!pubRef.current) return;
    pubRef.current = false; setPublished(false);
    try { await updateVariant(variantId, { isPublished: false }); } catch { /* тихо */ }
  }, [variantId]);
  const publish = async () => {
    try { await updateVariant(variantId, { isPublished: true }); await load(); toast.success('Вариант опубликован'); onMetaChanged && onMetaChanged(); }
    catch { toast.error('Ошибка публикации'); }
  };

  const setLink = async (webinarId) => {
    try {
      await updateVariant(variantId, { linkedLessonId: webinarId });
      toast.success(webinarId ? 'Вариант привязан к вебинару' : 'Связь убрана');
      setLinkOpen(false);
      await maybeUnpublish();
      await load();
      onMetaChanged && onMetaChanged();
    } catch { toast.error('Ошибка'); }
  };

  const toggle = useCallback((id) => { if (suppressClick.current) return; setOpenIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); }, []);

  // «Добавить номер» — сразу создаёт пустой номер-черновик и открывает его.
  const addNew = async () => {
    try {
      const idx = nomera.length;
      const res = await createHomework({ lessonId: variantId, title: `№${idx + 1}`, answerType: 'single', description: '', maxScore: defaultMax(variant?.examType, idx) });
      const newId = res.data?.id;
      await maybeUnpublish();
      await load();
      onMetaChanged && onMetaChanged();
      if (newId) setOpenIds(prev => new Set(prev).add(newId));
    } catch { toast.error('Не удалось добавить номер'); }
  };

  const onDuplicate = async (payload) => {
    try {
      await createHomework({ ...payload, lessonId: variantId });   // то же название, сразу под оригиналом
      toast.success('Номер продублирован');
      await maybeUnpublish();
      await load();
      onMetaChanged && onMetaChanged();
    } catch { toast.error('Не удалось дублировать'); }
  };

  const afterChange = useCallback(async () => { await maybeUnpublish(); await load(); onMetaChanged && onMetaChanged(); }, [maybeUnpublish, load, onMetaChanged]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const handleDragEnd = (e) => {
    suppressClick.current = true; setTimeout(() => { suppressClick.current = false; }, 80);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setNomera(prev => {
      const oldI = prev.findIndex(h => 'hw-' + h.id === active.id);
      const newI = prev.findIndex(h => 'hw-' + h.id === over.id);
      if (oldI < 0 || newI < 0) return prev;
      const next = arrayMove(prev, oldI, newI);
      reorderHomeworks(next.map(h => h.id))
        .then(() => { maybeUnpublish(); onMetaChanged && onMetaChanged(); })
        .catch(() => { toast.error('Не удалось сохранить порядок'); load(); });
      return next;
    });
  };

  const saveTitle = async () => {
    if (!title.trim() || title === variant?.title) return;
    try { await updateVariant(variantId, { title: title.trim() }); await maybeUnpublish(); onMetaChanged && onMetaChanged(); }
    catch { toast.error('Ошибка'); }
  };

  if (loading) return <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-3)', fontSize: 14 }}>Загрузка…</div>;
  if (!variant) return <div className="pg-empty">Вариант не найден</div>;

  const count = nomera.length;
  const maxPrimary = nomera.reduce((s, n) => s + (n.maxScore ?? 1), 0);
  const linkedTitle = variant.linkedLessonId ? (webinars.find(w => w.id === variant.linkedLessonId)?.title) : null;

  return (
    <div>
      <button onClick={onBack}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 13px', color: 'var(--text-2)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, marginBottom: 14 }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        К вариантам
      </button>

      <div className="cc-editor">
        <div className="cc-editor-head">
          <div className={`cc-editor-ic${!published ? ' draft' : ''}`} title={!published ? 'Черновик — не опубликовано' : undefined}>
            {!published
              ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg>
              : (icon || <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="2" /><line x1="9" y1="12" x2="15" y2="12" /><line x1="9" y1="16" x2="12" y2="16" /></svg>)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <input className="cc-inline-title" value={title} onChange={e => setTitle(e.target.value)} onBlur={saveTitle} />
            <div className="cc-editor-sub" style={{ paddingLeft: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {variant.examType ? `${variant.examType} · ` : ''}{count} {plural(count, ['номер', 'номера', 'номеров'])} · макс. {maxPrimary} {plural(maxPrimary, ['первичный', 'первичных', 'первичных'])}{linkedTitle ? ` · привязан: ${linkedTitle}` : ''}
            </div>
          </div>
          <button className={`cc-pub-btn ${published ? 'done' : 'active'}`} onClick={publish} disabled={published} title={published ? 'Опубликовано' : 'Опубликовать вариант'}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/><polyline points="9 13 12 10 15 13"/><line x1="12" y1="10" x2="12" y2="17"/></svg>
          </button>
          <button className={`cc-link-btn${variant.linkedLessonId ? ' on' : ''}`} onClick={() => setLinkOpen(true)} title={variant.linkedLessonId ? `Привязан: ${linkedTitle || 'вебинар'}` : 'Связать с вебинаром'}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
          </button>
        </div>

        <div className="cc-editor-body" style={{ gap: 8 }}>
          {count === 0 && <div className="cc-empty-leaf" style={{ padding: '26px 8px' }}>В варианте пока нет номеров. Нажмите «Добавить номер».</div>}

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={nomera.map(h => 'hw-' + h.id)} strategy={verticalListSortingStrategy}>
              {nomera.map((hw, i) => (
                <SortableNumber
                  key={hw.id}
                  homework={hw}
                  index={i}
                  showScore
                  expanded={openIds.has(hw.id)}
                  onToggle={toggle}
                  onDirty={maybeUnpublish}
                  onDeleted={afterChange}
                  onDuplicate={onDuplicate}
                />
              ))}
            </SortableContext>
          </DndContext>

          <button className="cc-add-section" onClick={addNew} style={{ marginTop: 4 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Добавить номер
          </button>
        </div>
      </div>

      <Modal isOpen={linkOpen} onClose={() => setLinkOpen(false)} title="Связать вариант с вебинаром">
        <p style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 14 }}>
          На выбранном вебинаре у ученика появится кнопка «Перейти к варианту», а в варианте — «К вебинару». Дата варианта возьмётся от вебинара.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 340, overflowY: 'auto' }}>
          {webinars.length === 0 && <div className="cc-empty-leaf">В курсе пока нет вебинаров</div>}
          {webinars.map(w => {
            const on = variant.linkedLessonId === w.id;
            return (
              <button key={w.id} onClick={() => setLink(w.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', padding: '11px 13px', borderRadius: 11, cursor: 'pointer', fontFamily: 'inherit',
                  border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`, background: on ? 'var(--primary-light)' : 'var(--card)', color: 'var(--text-1)' }}>
                <span style={{ width: 26, height: 26, borderRadius: 8, flexShrink: 0, background: 'var(--primary-light)', color: 'var(--primary-dark)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.title}</span>
                  <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-3)' }}>{w.section}</span>
                </span>
                {on && <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)' }}>✓</span>}
              </button>
            );
          })}
        </div>
        {variant.linkedLessonId && (
          <div className="form-actions" style={{ marginTop: 14 }}>
            <button className="action-btn secondary" onClick={() => setLink(null)}>Убрать связь</button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VariantNumbersPane;
